import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn, DateRangeValue } from "@outpave/ds";
import moment from "moment";
import { GET_TRANSACTION_ROWS } from "graphql/transaction";
import { IS_INTEGRATION_CONNECTED } from "graphql/integration";
import { RenderTransactionTypeChip } from "components/common/RenderTransactionType";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { formatCashFull } from "utils/formatCash";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { RenderCompliance } from "components/common/RenderCompliance";
import { RenderSyncStatus } from "components/common/RenderSyncStatus";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import { RenderTitleCase } from "components/common/RenderTitleCase";
import { Inline } from "components/layout/reusableLayouts";
import TransactionsType from "enums/TransactionsType";
import ViolationType from "enums/ViolationType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useAdminTools } from "hooks/useAdminTools";
import fullName from "utils/fullName";
import { transactionMerchantName } from "utils/transactions/transactionRow";
import { resolveDateRange } from "utils/dateRangeFilter";
import { amountQuery } from "utils/transactions/amountQuery";

export type TransactionFilterCriteria = {
  status?: string[] | null;
  type?: TransactionsType[] | null;
  compliances?: ViolationType[];
  searchTerm?: string;
  accountId?: string | null;
  dateRange?: DateRangeValue;
  amountRange?: { min?: number; max?: number };
};

type TransactionsTableProps = {
  filterCriteria: TransactionFilterCriteria;
  onRowClick: (transactionId: string) => void;
  /** Bump to refetch the rows already on screen. */
  refreshTransactions?: number;
  /** Only the viewer's own rows, as every /my list must be. */
  mine?: boolean;
};

type Data = {
  id: string;
  date: string;
  merchant: string;
  url?: string;
  logo?: string;
  cachedLogo?: string;
  type: TransactionsType;
  cardType?: string;
  cardLast4?: string;
  cardNickname?: string;
  amount: string;
  /** Signed figure behind `amount`: the sign is what colours the cell. */
  amountValue: number;
  owningAccountName?: string;
  owningAccountLast4?: string;
  userName: string;
  status: string;
  disputeStatus?: string;
  workflowStatus?: string;
  compliance?: unknown;
  complianceDisplay?: string;
  isIntegrationSynced?: boolean | null;
  isSyncEligible?: boolean;
  syncError?: string | null;
};

interface TransactionNode {
  id: string;
  merchant?: string | null;
  type: TransactionsType;
  status: string;
  disputeStatus?: string | null;
  amount: number;
  currency?: string | null;
  transactionDate: string | number;
  policyStatus?: unknown;
  policyStatusDisplay?: string | null;
  isIntegrationSynced?: boolean | null;
  isSyncEligible?: boolean;
  syncError?: string | null;
  workflowRequest?: { status?: string | null } | null;
  owningAccount?: { name?: string | null; last4?: string | null } | null;
  merchantData?: {
    name?: string | null;
    url?: string | null;
    logo?: string | null;
    cachedLogo?: string | null;
  } | null;
  card?: {
    type?: string | null;
    cardNumber?: string | null;
    nickname?: string | null;
  } | null;
  user?: { firstName?: string | null; lastName?: string | null } | null;
}

type TransactionConnection = {
  transactions?: { edges?: { node?: TransactionNode | null }[] } | null;
};

const formatDate = (raw: string | number): string => {
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "";
};

const extractTransactions = (data: TransactionConnection): Data[] =>
  (data?.transactions?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is TransactionNode => !!node)
    .map((node) => ({
      id: node.id,
      date: formatDate(node.transactionDate),
      merchant: transactionMerchantName(node),
      url: node.merchantData?.url ?? undefined,
      logo: node.merchantData?.logo ?? undefined,
      cachedLogo: node.merchantData?.cachedLogo ?? undefined,
      type: node.type,
      cardType: node.card?.type ?? undefined,
      cardLast4: node.card?.cardNumber ?? undefined,
      cardNickname: node.card?.nickname ?? undefined,
      amount: formatCashFull(node.amount, node.currency),
      amountValue: node.amount,
      owningAccountName: node.owningAccount?.name ?? undefined,
      owningAccountLast4: node.owningAccount?.last4 ?? undefined,
      userName: fullName(node.user),
      status: node.status,
      disputeStatus: node.disputeStatus ?? undefined,
      workflowStatus: node.workflowRequest?.status ?? undefined,
      compliance: node.policyStatus,
      complianceDisplay: node.policyStatusDisplay ?? undefined,
      isIntegrationSynced: node.isIntegrationSynced,
      isSyncEligible: node.isSyncEligible,
      syncError: node.syncError,
    }));

export const transactionsTableVariables = (
  filterCriteria: TransactionFilterCriteria,
  { mine, adminColumns }: { mine: boolean; adminColumns: boolean },
) => {
  const { startDate, endDate } = resolveDateRange(filterCriteria.dateRange);
  const searchTerm = filterCriteria.searchTerm?.trim() ?? "";
  return {
    first: DEFAULT_PAGINATION_SIZE,
    after: undefined,
    transactionStatus: filterCriteria.status?.length
      ? filterCriteria.status
      : undefined,
    type: filterCriteria.type,
    // A single character matches too much to be worth a request.
    searchTerm: searchTerm.length >= 2 ? searchTerm : undefined,
    compliances: filterCriteria.compliances,
    accountId: filterCriteria.accountId,
    amount: amountQuery(filterCriteria.amountRange),
    startDate,
    endDate,
    ...(mine && { mine: true }),
    withWorkflow: true,
    // Purchaser, Account and Synced cost the server a lookup per row, so only a viewer who sees them fetches them.
    withAdminColumns: adminColumns,
  };
};

/** Purchaser, Account and Synced are Manager and above, off on /my, as the drawer's admin tools are. */
export const transactionsTableColumns = (
  t: ReturnType<typeof useTranslation>["t"],
  {
    adminColumns,
    accountingConnected,
  }: { adminColumns: boolean; accountingConnected: boolean },
): DataColumn<Data>[] => [
  {
    id: "date",
    header: t("Date"),
    width: 0,
    cell: (transaction) => (
      <Typography variant="body2" component="span" noWrap>
        {transaction.date}
      </Typography>
    ),
  },
  {
    id: "merchant",
    header: t("Merchant"),
    maxWidth: 160,
    cell: (transaction) => (
      <RenderMerchantDisplay
        merchant={transaction.merchant}
        url={transaction.url}
        logo={transaction.logo}
        cachedLogo={transaction.cachedLogo}
      />
    ),
  },
  ...(adminColumns
    ? ([
        {
          id: "userName",
          header: t("Purchaser"),
          maxWidth: 128,
          cell: (transaction) => transaction.userName,
        },
        {
          id: "owningAccount",
          header: t("Account"),
          maxWidth: 160,
          cell: (transaction) => (
            <RenderTitleCase
              text={[
                transaction.owningAccountName,
                transaction.owningAccountLast4,
              ]}
            />
          ),
        },
      ] as DataColumn<Data>[])
    : []),
  {
    id: "type",
    header: t("Type"),
    width: 0,
    cell: (transaction) => (
      <Inline gap="var(--space-2)" align="center" wrap={false}>
        <RenderTransactionTypeChip
          transactionType={transaction.type}
          cardType={transaction.cardType}
        />
        {transaction.cardLast4 && (
          <Typography
            variant="body2"
            component="span"
            noWrap
            title={transaction.cardNickname}
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {`••••${transaction.cardLast4}`}
          </Typography>
        )}
      </Inline>
    ),
  },
  {
    id: "amount",
    header: t("Amount"),
    align: "center",
    width: 175,
    cell: (transaction) => ({
      content: (
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: "var(--type-weight-semibold)",
            // Money coming back reads green; everything else takes the
            // table's own ink rather than a second colour meaning "spend".
            color:
              transaction.amountValue > 0 ? "var(--color-positive)" : undefined,
          }}
        >
          {transaction.amount}
        </Typography>
      ),
      align: "right",
    }),
  },
  {
    id: "status",
    header: t("Status"),
    align: "center",
    width: 0,
    cell: (transaction) => (
      <RenderTransactionStatus
        transactionStatus={transaction.status}
        disputeStatus={transaction.disputeStatus}
        workflowStatus={transaction.workflowStatus}
        transactionType={transaction.type}
        smallPill
      />
    ),
  },
  {
    id: "compliance",
    header: t("Compliance"),
    align: "center",
    width: 0,
    cell: (transaction) => (
      <RenderCompliance
        policyStatus={transaction.compliance}
        policyStatusDisplay={transaction.complianceDisplay}
        hideTerminal
      />
    ),
  },
  ...(adminColumns && accountingConnected
    ? ([
        {
          id: "isIntegrationSynced",
          header: t("Synced"),
          align: "center",
          width: 0,
          cell: (transaction) => (
            <RenderSyncStatus
              isIntegrationSynced={transaction.isIntegrationSynced}
              isSyncEligible={transaction.isSyncEligible ?? true}
              syncError={transaction.syncError}
            />
          ),
        },
      ] as DataColumn<Data>[])
    : []),
];

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  filterCriteria,
  onRowClick,
  refreshTransactions,
  mine = false,
}) => {
  const { t } = useTranslation();
  const adminColumns = useAdminTools();

  // The column only means something once a ledger is on the other end, so it
  // is absent rather than empty when no accounting integration is connected.
  const { data: integrationConnection } = useQuery(IS_INTEGRATION_CONNECTED, {
    skip: !adminColumns,
  });
  const accountingConnected = Boolean(
    integrationConnection?.isIntegrationConnected,
  );

  // Memoised rather than rebuilt: `useInfiniteScroll` stringifies this object
  // to decide when to reset, so a fresh object every render refetches.
  const initialVariables = useMemo(
    () => transactionsTableVariables(filterCriteria, { mine, adminColumns }),
    [filterCriteria, mine, adminColumns],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_TRANSACTION_ROWS,
      initialVariables,
      extractData: extractTransactions,
      refetchIndex: refreshTransactions,
    });

  const columns = useMemo(
    () => transactionsTableColumns(t, { adminColumns, accountingConnected }),
    [t, adminColumns, accountingConnected],
  );

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(transaction) => transaction.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(transaction) => onRowClick(transaction.id)}
        onScroll={handleScroll}
        emptyState={t("No transactions found")}
        loadingMoreText={t("Loading more transactions")}
        stickyHeader
      />
    </Box>
  );
};

export default TransactionsTable;
